import React, { useState, useMemo, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import { CheckCircle2, Hand } from 'lucide-react';
import { InstantFeedback } from './board/InstantFeedback';

const textOf = (item) => (typeof item === 'string' ? item : item.text || item.label || '');

// Learner builds the sequence step by step: tap (or drag) the item that comes next.
const SortOrderElement = ({ config, element, progress, onComplete, onSaveData, isCompleted }) => {
  const sc = config.sort_order || config || {};
  const rawItems = sc.items || [];

  const correctOrder = useMemo(() => {
    const hasPos = rawItems.some(it => typeof it === 'object' && it.correct_position !== undefined);
    const sorted = hasPos ? [...rawItems].sort((a, b) => (a.correct_position ?? 0) - (b.correct_position ?? 0)) : rawItems;
    return sorted.map(textOf);
  }, [element?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [pool] = useState(() => {
    let s = [...correctOrder];
    for (let n = 0; n < 10 && s.join('|') === correctOrder.join('|') && s.length > 1; n++) s = [...correctOrder].sort(() => Math.random() - 0.5);
    return s;
  });
  const [sequence, setSequence] = useState(() => {
    if (isCompleted) return [...correctOrder];
    const saved = progress?.element_progress?.[element?.id]?.order || [];
    const valid = [];
    for (let i = 0; i < saved.length && saved[i] === correctOrder[i]; i++) valid.push(saved[i]);
    return valid;
  });
  const [feedback, setFeedback] = useState(null);
  const [wrongItem, setWrongItem] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const remaining = pool.filter(t => !sequence.includes(t));
  const done = correctOrder.length > 0 && sequence.length === correctOrder.length;

  const choose = (text) => {
    if (!text || sequence.includes(text)) return;
    const ok = correctOrder[sequence.length] === text;
    setFeedback({ ok, key: Date.now() });
    clearTimeout(timer.current);
    if (!ok) { setWrongItem(text); timer.current = setTimeout(() => setWrongItem(null), 1200); return; }
    const next = [...sequence, text];
    setSequence(next);
    const allDone = next.length === correctOrder.length;
    onSaveData({ order: next, correct: allDone });
    if (allDone && !isCompleted) { onComplete(); toast.success('Correct order!'); }
  };

  return (
    <div className="space-y-4" data-testid="sort-order-element">
      {sc.instructions && <p className="text-sm text-slate-600">{sc.instructions}</p>}
      <InstantFeedback feedback={feedback} testId="sort-feedback" />

      <ol
        className="space-y-2"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); choose(e.dataTransfer.getData('text/plain')); }}
        data-testid="sort-sequence"
      >
        {correctOrder.map((_, i) => {
          const t = sequence[i];
          const isNext = i === sequence.length;
          return (
            <li
              key={`slot-${i}`}
              className={`flex items-center gap-3 min-h-[48px] p-3 rounded-lg border-2 transition-colors ${
                t ? 'border-green-300 bg-green-50' : isNext ? 'border-[#20acf6] border-dashed bg-[#20acf6]/5' : 'border-slate-200 border-dashed bg-slate-50'
              }`}
              data-testid={`sort-slot-${i}`}
            >
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${t ? 'bg-green-600 text-white' : 'bg-slate-200 text-slate-600'}`}>{i + 1}</span>
              {t ? (
                <><span className="text-sm flex-1 text-green-900">{t}</span><CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" /></>
              ) : (
                <span className="text-xs text-slate-400">{isNext ? 'Tap the step that comes next' : ''}</span>
              )}
            </li>
          );
        })}
      </ol>

      {done ? (
        <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-green-800 font-medium flex items-center gap-2" data-testid="sort-success">
          <CheckCircle2 className="h-5 w-5" /> Correct order — well done!
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-slate-500 flex items-center gap-1.5"><Hand className="h-3.5 w-3.5" /> Tap the item that comes next (or drag it into the list on desktop).</p>
          <div className="grid gap-2" data-testid="sort-pool">
            {remaining.map((t) => (
              <button
                key={t}
                type="button"
                draggable
                onDragStart={(e) => e.dataTransfer.setData('text/plain', t)}
                onClick={() => choose(t)}
                className={`min-h-[44px] text-left px-4 py-2 rounded-lg border-2 text-sm transition-colors ${
                  wrongItem === t ? 'border-red-400 bg-red-50 animate-[shake_0.3s_ease-in-out]' : 'border-slate-200 bg-white hover:border-[#20acf6]/60 cursor-grab'
                }`}
                data-testid={`sort-item-${pool.indexOf(t)}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SortOrderElement;
